import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import {
  DEFAULT_TIMEOUT_SECONDS,
  KIOSK_FLOW_STEPS,
  type DraftIssue,
  type DraftStep,
  type KioskLayoutOption,
} from '../../types/kiosk-flow-types';
import { isKnownStep } from '../../utils/flow-steps';
import { LayoutSelect } from './LayoutSelect';

// Radix Select items can't use '' as a value
const NO_TARGET = '__none__';

const SAVE_BORDER = 'border-red-500 focus:ring-red-500 focus-visible:ring-red-500';
const PUBLISH_BORDER = 'border-amber-500 focus:ring-amber-500 focus-visible:ring-amber-500';

// Red for what blocks Save, amber for what only blocks Publish
function FieldIssues({ issues }: { issues: DraftIssue[] }) {
  return (
    <>
      {issues.map((issue, index) => (
        <p key={index} className={`mt-1 text-xs ${issue.blocks === 'save' ? 'text-red-600' : 'text-amber-600'}`}>
          {issue.message}
        </p>
      ))}
    </>
  );
}

function borderFor(issues: DraftIssue[]): string {
  if (issues.some((i) => i.blocks === 'save')) return SAVE_BORDER;
  return issues.length > 0 ? PUBLISH_BORDER : '';
}

interface FlowStepRowProps {
  // 1-based position — the "Step N" in validation messages
  index: number;
  row: DraftStep;
  allRows: DraftStep[];
  layouts: KioskLayoutOption[];
  isLoadingLayouts: boolean;
  // This row's issues from validateDraft
  issues: DraftIssue[];
  disabled?: boolean;
  onChange: (changes: Partial<Omit<DraftStep, 'id'>>) => void;
  onRemove: () => void;
}

export function FlowStepRow({
  index,
  row,
  allRows,
  layouts,
  isLoadingLayouts,
  issues,
  disabled,
  onChange,
  onRemove,
}: FlowStepRowProps) {
  const others = allRows.filter((r) => r.id !== row.id);
  const usedSteps = new Set(others.map((r) => r.step));
  const usedLayouts = new Set(others.map((r) => r.layoutName));
  const targetOptions = others.map((r) => r.step).filter(Boolean);
  // A stored target that's no longer a step in the flow still shows, so the user knows what to replace
  const danglingTarget = row.timeoutTarget !== '' && !targetOptions.includes(row.timeoutTarget);
  const unknownStep = row.step !== '' && !isKnownStep(row.step);
  // An empty field shows its placeholder — the summary lists it, so it isn't repeated on the row
  const fieldIssues = (field: DraftIssue['field'], value: string) =>
    value === '' ? [] : issues.filter((i) => i.field === field);
  const stepIssues = fieldIssues('step', row.step);
  const layoutIssues = fieldIssues('layoutName', row.layoutName);
  const targetIssues = fieldIssues('timeoutTarget', row.timeoutTarget);
  const timeoutIssues = fieldIssues('timeoutSeconds', row.timeoutSeconds);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled,
  });

  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`border-t align-top ${isDragging ? 'relative z-10 bg-white shadow-md' : ''}`}
    >
      {/* Pinned: cancels the row's drag offset so the numbers stay put while rows move past them */}
      <td
        className="w-8 py-2 pl-3 text-xs leading-8 text-gray-500 tabular-nums"
        style={{ transform: transform ? `translate3d(0, ${-transform.y}px, 0)` : undefined, transition }}
      >
        {index}
      </td>
      <td className="w-8 py-2 pl-2">
        <button
          type="button"
          ref={setActivatorNodeRef}
          className="flex h-8 w-6 cursor-grab touch-none items-center justify-center rounded text-gray-400 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 active:cursor-grabbing"
          aria-label="Drag to reorder"
          disabled={disabled}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="px-3 py-2">
        <Select value={row.step} onValueChange={(step) => onChange({ step })} disabled={disabled}>
          <SelectTrigger className={`h-8 ${borderFor(stepIssues)}`} aria-label="Step" aria-invalid={stepIssues.length > 0}>
            <SelectValue placeholder="Select step" />
          </SelectTrigger>
          <SelectContent>
            {/* Keeps a stored, no-longer-valid name visible so the user knows what to replace */}
            {unknownStep && (
              <SelectItem value={row.step} disabled>
                {row.step}
              </SelectItem>
            )}
            {KIOSK_FLOW_STEPS.map((step) => (
              <SelectItem key={step} value={step} disabled={usedSteps.has(step)}>
                {step}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldIssues issues={stepIssues} />
      </td>
      <td className="px-3 py-2">
        <LayoutSelect
          value={row.layoutName}
          layouts={layouts}
          usedLayouts={usedLayouts}
          isLoading={isLoadingLayouts}
          missing={layoutIssues.some((i) => i.blocks === 'publish')}
          className={borderFor(layoutIssues)}
          disabled={disabled}
          onChange={(layoutName) => onChange({ layoutName })}
        />
        <FieldIssues issues={layoutIssues} />
      </td>
      <td className="px-3 py-2">
        <Select
          value={row.timeoutTarget || NO_TARGET}
          onValueChange={(value) => onChange({ timeoutTarget: value === NO_TARGET ? '' : value })}
          disabled={disabled}
        >
          <SelectTrigger
            className={`h-8 ${borderFor(targetIssues)}`}
            aria-label="Timeout target"
            aria-invalid={targetIssues.length > 0}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_TARGET}>None</SelectItem>
            {danglingTarget && (
              <SelectItem value={row.timeoutTarget} disabled>
                {row.timeoutTarget}
              </SelectItem>
            )}
            {targetOptions.map((step) => (
              <SelectItem key={step} value={step}>
                {step}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldIssues issues={targetIssues} />
      </td>
      <td className="px-3 py-2">
        <Input
          className={`h-8 w-24 text-gray-900 placeholder:text-gray-300 ${borderFor(timeoutIssues)}`}
          inputMode="decimal"
          aria-label="Timeout in seconds"
          aria-invalid={timeoutIssues.length > 0}
          placeholder={String(DEFAULT_TIMEOUT_SECONDS)}
          value={row.timeoutSeconds}
          disabled={disabled}
          onChange={(e) => onChange({ timeoutSeconds: e.target.value.trim() })}
        />
        <FieldIssues issues={timeoutIssues} />
      </td>
      <td className="px-3 py-2 text-right">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-gray-500 hover:text-red-600"
          aria-label="Delete step"
          disabled={disabled}
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}
